import * as React from 'react';
import { Control, FieldValues, useController, ValidationRule } from 'react-hook-form';
import { RegexpUtility } from '../utilities/regexp';
import { ErrorMessage } from './error-message';
import { InputFormLayout } from './input-form-layout';
import { Label } from './label';

export interface IInputFormProps {
  label: string;
  labelFor: string;
  required?: boolean;
}

export const InputForm = ({
  label,
  labelFor,
  required = false,
  children
}: React.PropsWithChildren<IInputFormProps>) => {
  const labelProps = { label, labelFor, required };
  return <InputFormLayout left={<Label className='mr-0 mr-md-3' {...labelProps} />} right={children} />;
};

export interface IInputFormFactoryForm {
  name: string;
  control: Control<FieldValues>;
}

export interface IInputFormFactoryValidations {
  min?: ValidationRule<number | string>;
  max?: ValidationRule<number | string>;
  maxLength?: ValidationRule<number>;
  minLength?: ValidationRule<number>;
  pattern?: ValidationRule<RegExp>;
  validate?: { [key: string]: (value: string) => boolean | string };
}

export enum InputFormFactoryType {
  Text = 'text',
  TextArea = 'textarea',
  Email = 'email'
}

export interface IInputFormFactoryProps {
  label: string;
  name: string;
  type?: InputFormFactoryType;
  form: IInputFormFactoryForm;
  required?: boolean;
  validations?: IInputFormFactoryValidations;
}

export const InputFormFactory = (props: IInputFormFactoryProps) => {
  const { label, name, required = false, form, type = InputFormFactoryType.Text, validations = {} } = props;
  const inputId = `${form.name}-${name}`;
  const DefaultValidationOptions = {
    ...(type === 'email'
      ? {
          pattern: {
            value: RegexpUtility.Email,
            message: `${label} is not a valid email address`
          }
        }
      : {})
  };
  const {
    field,
    fieldState: { error }
  } = useController({
    name,
    control: form.control,
    rules: {
      required,
      ...{
        ...DefaultValidationOptions,
        ...validations
      }
    }
  });
  const renderInput = React.useCallback(() => {
    const attrs = {
      id: inputId,
      ...field,
      'aria-required': required
    };
    if (!type || type === InputFormFactoryType.Text) {
      return <input className='form-control' {...attrs} />;
    }
    switch (type) {
      case InputFormFactoryType.TextArea:
        return <textarea className='form-control' {...attrs}></textarea>;
      case InputFormFactoryType.Email:
        return <input className='form-control' type='text' {...attrs}></input>;
      default:
        return null;
    }
  }, [type]);
  return (
    <InputForm label={label} labelFor={inputId} required={required}>
      {renderInput()}
      <ErrorMessage label={label} error={error} />
    </InputForm>
  );
};
